// Google Places Autocomplete hook using the Places API
// Uses legacy AutocompleteService + PlacesService which are stable and widely available
// See: https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete-addressform

import { setOptions, importLibrary } from "@googlemaps/js-api-loader";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDebounce } from "./use-debounce";

export interface PlacePrediction {
  placeId: string;
  description: string;
  mainText: string;
  secondaryText: string;
}

export interface ParsedAddress {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

type AutocompleteService = google.maps.places.AutocompleteService;
type AutocompleteSessionToken = google.maps.places.AutocompleteSessionToken;
type PlacesService = google.maps.places.PlacesService;

// Singleton promise so we only load the places library once
let placesLibraryPromise: Promise<google.maps.PlacesLibrary> | null = null;
let optionsSet = false;

function loadPlacesLibrary(): Promise<google.maps.PlacesLibrary> {
  if (placesLibraryPromise) return placesLibraryPromise;

  const apiKey =
    import.meta.env.VITE_GOOGLE_MAPS_API_KEY ||
    "AIzaSyAxs6cioKYDPmXeUNHbW1HvUIZk-UWMhgI";
  if (!apiKey) {
    return Promise.reject(new Error("VITE_GOOGLE_MAPS_API_KEY is not set"));
  }

  if (!optionsSet) {
    // language: "en" forces Google to return address components and prediction
    // descriptions in English regardless of the creator's browser locale, so
    // "Italia" / "台灣" / "Lombardia" never leak into our form state and down
    // to Shopify (EPD-7994). Consistent with the rest of our English-only form.
    setOptions({
      key: apiKey,
      libraries: ["places"],
      language: "en",
    });
    optionsSet = true;
  }

  placesLibraryPromise = importLibrary("places").catch(
    (err: google.maps.MapsServerError) => {
      // Reset so subsequent calls retry instead of returning the cached rejection
      placesLibraryPromise = null;
      optionsSet = false;
      throw err;
    },
  );
  return placesLibraryPromise;
}

export function parseAddressComponents(
  components: google.maps.GeocoderAddressComponent[],
): ParsedAddress {
  const get = (type: string, useShort = false): string => {
    const comp = components.find((c) => c.types.includes(type));
    if (!comp) return "";
    return useShort ? comp.short_name : comp.long_name;
  };

  const streetNumber = get("street_number");
  const route = get("route");
  const addressLine1 = [streetNumber, route].filter(Boolean).join(" ");
  const addressLine2 = get("subpremise");
  const city =
    get("locality") || get("sublocality_level_1") || get("postal_town");
  // EPD-11401: use short_name (ISO codes) for state + country, NOT long_name. Google
  // returns long_name in the user's browser locale (e.g. a Spanish creator gets
  // "Estados Unidos" / "Nueva York"), which the form then persisted verbatim and
  // fulfillment (Shopify/carriers) rejected — stuck orders. short_name is the
  // locale-independent ISO subdivision/country code ("NY" / "US"), which is what the
  // country/province dropdowns and normalizers key off. `resolveProvinceCode` matches
  // by code so this stays consistent with the dropdown selection.
  const state = get("administrative_area_level_1", true);
  const zip = get("postal_code");
  const country = get("country", true);

  return { addressLine1, addressLine2, city, state, zip, country };
}

// Cached geolocation — requested once per page load, shared across hook instances
let userLocationPromise: Promise<google.maps.LatLngBoundsLiteral | null> | null =
  null;

function getUserLocationBias(): Promise<google.maps.LatLngBoundsLiteral | null> {
  if (userLocationPromise) return userLocationPromise;

  userLocationPromise = new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        // ~50 km bias radius around the user
        const offset = 0.5;
        resolve({
          south: latitude - offset,
          west: longitude - offset,
          north: latitude + offset,
          east: longitude + offset,
        });
      },
      () => {
        // Permission denied or error — degrade gracefully
        resolve(null);
      },
      { timeout: 5000, maximumAge: 300000 },
    );
  });

  return userLocationPromise;
}

export function useGooglePlacesAutocomplete() {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const autocompleteServiceRef = useRef<AutocompleteService | null>(null);
  const placesServiceRef = useRef<PlacesService | null>(null);
  const sessionTokenRef = useRef<AutocompleteSessionToken | null>(null);
  const libraryRef = useRef<google.maps.PlacesLibrary | null>(null);
  const locationBiasRef = useRef<google.maps.LatLngBounds | null>(null);
  const requestIdRef = useRef(0);

  const debouncedInput = useDebounce(inputValue, 300);

  const ensureServices = useCallback(async (): Promise<boolean> => {
    if (autocompleteServiceRef.current && placesServiceRef.current) {
      return true;
    }

    try {
      const lib = await loadPlacesLibrary();
      libraryRef.current = lib;
      autocompleteServiceRef.current = new lib.AutocompleteService();
      // PlacesService needs a div (attribution container)
      const attrDiv = document.createElement("div");
      placesServiceRef.current = new lib.PlacesService(attrDiv);
      sessionTokenRef.current = new lib.AutocompleteSessionToken();

      // Build location bias from user's geolocation (non-blocking)
      const coreLib = (await importLibrary("core")) as google.maps.CoreLibrary;
      const bounds = await getUserLocationBias();
      if (bounds) {
        locationBiasRef.current = new coreLib.LatLngBounds(
          { lat: bounds.south, lng: bounds.west },
          { lat: bounds.north, lng: bounds.east },
        );
      }

      return true;
    } catch (err) {
      console.error("[Places] Failed to load Google Places library:", err);
      return false;
    }
  }, []);

  const fetchPredictionsInternal = useCallback(
    async (input: string) => {
      const currentRequestId = ++requestIdRef.current;

      if (!input || input.length < 3) {
        setPredictions([]);
        return;
      }

      const ready = await ensureServices();
      if (!ready || !autocompleteServiceRef.current) return;
      // Another request was fired while we awaited services
      if (currentRequestId !== requestIdRef.current) return;

      setIsLoading(true);
      try {
        const request: google.maps.places.AutocompletionRequest = {
          input,
          types: ["address"],
          sessionToken: sessionTokenRef.current ?? undefined,
        };

        // Bias results toward the user's location if available
        if (locationBiasRef.current) {
          request.bounds = locationBiasRef.current;
        }

        const response =
          await autocompleteServiceRef.current.getPlacePredictions(request);

        // Ignore stale response if a newer request has been fired
        if (currentRequestId !== requestIdRef.current) return;

        const mapped: PlacePrediction[] = (response?.predictions ?? []).map(
          (p: google.maps.places.AutocompletePrediction) => ({
            placeId: p.place_id,
            description: p.description,
            mainText: p.structured_formatting.main_text,
            secondaryText: p.structured_formatting.secondary_text,
          }),
        );
        setPredictions(mapped);
      } catch (err) {
        if (currentRequestId !== requestIdRef.current) return;
        console.error("[Places] Failed to fetch predictions:", err);
        setPredictions([]);
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [ensureServices],
  );

  // Trigger fetch when debounced value changes
  useEffect(() => {
    fetchPredictionsInternal(debouncedInput);
  }, [debouncedInput, fetchPredictionsInternal]);

  const fetchPredictions = useCallback((input: string) => {
    setInputValue(input);
  }, []);

  const clearPredictions = useCallback(() => {
    setPredictions([]);
    setInputValue("");
  }, []);

  const selectPrediction = useCallback(
    async (placeId: string): Promise<ParsedAddress | null> => {
      const ready = await ensureServices();
      const placesService = placesServiceRef.current;
      if (!ready || !placesService) return null;

      return new Promise<ParsedAddress | null>((resolve) => {
        placesService.getDetails(
          {
            placeId,
            fields: ["address_components"],
            sessionToken: sessionTokenRef.current ?? undefined,
          },
          (result, status) => {
            // Reset session token (ends the billing session)
            if (libraryRef.current) {
              sessionTokenRef.current =
                new libraryRef.current.AutocompleteSessionToken();
            }

            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              result?.address_components
            ) {
              resolve(parseAddressComponents(result.address_components));
            } else {
              console.error("[Places] getDetails failed:", status);
              resolve(null);
            }
          },
        );
      });
    },
    [ensureServices],
  );

  return {
    predictions,
    isLoading,
    fetchPredictions,
    selectPrediction,
    clearPredictions,
  };
}
